import { useState, useMemo, useRef, useEffect, useCallback, useDeferredValue } from 'react';
import { useParams } from 'react-router-dom';
import {
  Text,
  Spinner,
  Button,
  Tooltip,
  mergeClasses,
  Input,
  Select,
  ToggleButton,
} from '@coreai/fluentui-react';
import {
  DismissRegular,
  PlayFilled,
  SearchRegular,
  HistoryRegular,
  FilterDismissRegular,
  ChatRegular,
  SelectAllOnRegular,
} from '@fluentui/react-icons';
import { useInfiniteRuns } from '@/hooks/useMonitoringData';
import type { Run, RunStatus, VersionFilter } from '@/types/monitoring';
import {
  FiltersPanel,
  type FiltersPanelStatusOption,
  type FiltersPanelSegmentedOption,
} from '@/components/MonitoringView/shared/FiltersPanel';
import { TriggerWorkflowDialog } from '../TriggerWorkflowDialog';
import { useWorkflowStore } from '@/stores/workflowStore';
import { useDraftModeStore, selectIsDraftMode } from '@/stores/draftModeStore';
import { useStreamingRun } from '@/hooks/useStreamingRun';
import { useDraftSave } from '@/hooks/useDraftSave';
import { useAppPermission } from '@/hooks/useAppPermission';
import { useNotificationStore } from '@/stores/notificationStore';
import {
  formatBulkRetryNotification,
  getRunRetryEligibility,
  type BulkRetryItem,
  type RunRetryController,
} from '@/components/MonitoringView/shared/resubmit';

import {WorkflowRunsPanelStyles } from './WorkflowRunsPanel.styles'
import { RunCardSkeleton } from './RunCardSkeleton';
import { RunCard } from './RunCard';
import { RunSelectionToolbar } from './RunSelectionToolbar';
import { MonitoringListHeader } from '@/components/MonitoringView/shared/MonitoringListHeader';
import { MonitoringEmptyState } from '@/components/MonitoringView/shared/MonitoringEmptyState';

type WorkflowRunsPanelProps = {
  selectedRunId: string | null;
  selectedExecutionId: string | null;
  onSelectRun: (runId: string) => void;
  onSelectExecution: (executionId: string) => void;
  runRetry: RunRetryController;
  /** If true, hides the Run button (for conversational workflows where New Chat starts a run) */
  hideRunButton?: boolean;
  /** Called when user clicks Start Chat (for conversational workflows) */
  onStartChat?: (() => void) | undefined;
  /** Whether a new chat is being started */
  isStartingChat?: boolean;
};

type DatePreset = 'all' | '1h' | '6h' | '24h' | '7d' | '30d' | 'custom';


const DATE_PRESETS: { value: DatePreset; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: '1h', label: '1h' },
  { value: '6h', label: '6h' },
  { value: '24h', label: '24h' },
  { value: '7d', label: '7d' },
  { value: '30d', label: '30d' },
  { value: 'custom', label: 'Custom' },
];

type TimeUnit = 'hours' | 'days';

const MS_PER_HOUR = 60 * 60 * 1000;
const MS_PER_DAY = 24 * MS_PER_HOUR;

const getDateCutoff = (
  preset: DatePreset,
  customValue?: number,
  customUnit?: TimeUnit
): Date | null => {
  if (preset === 'all') return null;
  const now = new Date();

  if (preset === 'custom' && customValue && customUnit) {
    const multiplier = customUnit === 'hours' ? MS_PER_HOUR : MS_PER_DAY;
    return new Date(now.getTime() - customValue * multiplier);
  }

  switch (preset) {
    case '1h':
      return new Date(now.getTime() - 1 * MS_PER_HOUR);
    case '6h':
      return new Date(now.getTime() - 6 * MS_PER_HOUR);
    case '24h':
      return new Date(now.getTime() - 24 * MS_PER_HOUR);
    case '7d':
      return new Date(now.getTime() - 7 * MS_PER_DAY);
    case '30d':
      return new Date(now.getTime() - 30 * MS_PER_DAY);
    default:
      return null;
  }
};



const STATUS_OPTIONS: ReadonlyArray<FiltersPanelStatusOption<RunStatus>> = [
  { value: 'succeeded', label: 'Succeeded', tone: 'succeeded' },
  { value: 'failed', label: 'Failed', tone: 'failed' },
  { value: 'running', label: 'Running', tone: 'running' },
  { value: 'cancelled', label: 'Cancelled', tone: 'cancelled' },
];

const VERSION_OPTIONS: ReadonlyArray<FiltersPanelSegmentedOption<VersionFilter>> = [
  { value: 'all', label: 'All' },
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
];

const DATE_PRESET_OPTIONS: ReadonlyArray<FiltersPanelSegmentedOption<DatePreset>> =
  DATE_PRESETS.map((p) => ({ value: p.value, label: p.label }));

const getTimeRangeLabel = (preset: DatePreset, customValue: number, customUnit: TimeUnit): string => {
  if (preset === 'all') return '';
  if (preset === 'custom') return `Last ${customValue} ${customUnit}`;
  return `Last ${preset}`;
};

export const WorkflowRunsPanel = ({
  selectedRunId,
  onSelectRun,
  runRetry,
  hideRunButton = false,
  onStartChat,
  isStartingChat = false,
}: WorkflowRunsPanelProps) => {
  const styles = WorkflowRunsPanelStyles();
  const { workflowName, appName, projectName } = useParams<{ workflowName: string; appName: string; projectName: string }>();
  const workflow = useWorkflowStore((s) => s.workflow);
  const isDraftMode = useDraftModeStore(selectIsDraftMode);
  const { canEdit: hasEditPermission } = useAppPermission();

  // Streaming run support — provides streaming draft trigger for TriggerWorkflowDialog
  const { handleStreamingDraftTrigger } = useStreamingRun();

  // Draft save for running draft (saves immediately before run)
  const { save: saveDraft } = useDraftSave({
    workflowName: workflowName ?? '',
  });

  // Callback to save draft before running
  const handleSaveDraft = useCallback(async (): Promise<boolean> => {
    try {
      const result = await saveDraft();
      return result.success;
    } catch {
      return false;
    }
  }, [saveDraft]);
  const {
    data: allRuns,
    isLoading,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isRefetching,
  } = useInfiniteRuns();
  const [selectedStatuses, setSelectedStatuses] = useState<RunStatus[]>([]);
  const [versionFilter, setVersionFilter] = useState<VersionFilter>('all');
  const [datePreset, setDatePreset] = useState<DatePreset>('all');
  const [customTimeValue, setCustomTimeValue] = useState<number>(12);
  const [customTimeUnit, setCustomTimeUnit] = useState<TimeUnit>('hours');
  const [isTriggerDialogOpen, setIsTriggerDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const deferredSearchQuery = useDeferredValue(searchQuery);
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const runsListRef = useRef<HTMLDivElement>(null);
  const runCardRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Multi-select for bulk whole-run retry.
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [checkedRunIds, setCheckedRunIds] = useState<ReadonlySet<string>>(() => new Set());
  const selectionScopeKey = `${projectName ?? ''}/${appName ?? ''}/${workflowName ?? ''}`;

  useEffect(() => {
    setIsSelectionMode(false);
    setCheckedRunIds(new Set());
  }, [selectionScopeKey]);

  const { retry: retryRuns, isPending: isBulkRetryPending, cancel: cancelRetry } = runRetry;
  useEffect(() => cancelRetry, [cancelRetry]);

  const toggleRunChecked = useCallback((runId: string) => {
    setCheckedRunIds((prev) => {
      const next = new Set(prev);
      if (next.has(runId)) next.delete(runId);
      else next.add(runId);
      return next;
    });
  }, []);

  const handleRowActivate = isSelectionMode ? toggleRunChecked : onSelectRun;

  const handleSelectionModeChange = useCallback(() => {
    setIsSelectionMode((prev) => !prev);
    setCheckedRunIds(new Set());
  }, []);

  const currentTriggers = workflow?.definition?.triggers;
  const checkedRuns = useMemo(
    () => (allRuns ?? []).filter((run: Run) => checkedRunIds.has(run.id)),
    [allRuns, checkedRunIds],
  );
  const eligibleRetryItems = useMemo((): BulkRetryItem[] => {
    const items: BulkRetryItem[] = [];
    for (const run of checkedRuns) {
      const eligibility = getRunRetryEligibility(run, { hasEditPermission, currentTriggers });
      if (eligibility.eligible) items.push({ runId: run.id, triggerName: eligibility.triggerName });
    }
    return items;
  }, [checkedRuns, hasEditPermission, currentTriggers]);

  const handleBulkRetry = useCallback(async () => {
    const ineligibleCount = checkedRuns.length - eligibleRetryItems.length;
    const summary = await retryRuns(eligibleRetryItems);
    if (!summary) return;
    if (summary.succeeded.length > 0) {
      const succeeded = new Set(summary.succeeded);
      setCheckedRunIds((prev) => new Set([...prev].filter((id) => !succeeded.has(id))));
    }
    useNotificationStore.getState().addNotification(formatBulkRetryNotification(summary, ineligibleCount));
  }, [checkedRuns.length, eligibleRetryItems, retryRuns]);
  // Intersection Observer for infinite scroll
  useEffect(() => {
    const sentinel = loadMoreRef.current;
    const root = runsListRef.current;
    if (!sentinel || !root) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { root, rootMargin: '100px' }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Calculate active filter count
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (selectedStatuses.length > 0) count += selectedStatuses.length;
    if (versionFilter !== 'all') count += 1;
    if (datePreset !== 'all') count += 1;
    if (deferredSearchQuery.trim()) count += 1;
    return count;
  }, [selectedStatuses, versionFilter, datePreset, deferredSearchQuery]);

  const hasActiveFilters = activeFilterCount > 0;

  // Filter runs
  const filteredRuns = useMemo(() => {
    if (!allRuns) return [];

    // Hoist values that are constant for the entire pass so we don't recompute them per run.
    const dateCutoff = getDateCutoff(datePreset, customTimeValue, customTimeUnit);
    const dateCutoffMs = dateCutoff ? dateCutoff.getTime() : null;
    const trimmedQuery = deferredSearchQuery.trim().toLowerCase();
    const hasQuery = trimmedQuery.length > 0;
    const hasStatusFilter = selectedStatuses.length > 0;
    const statusSet = hasStatusFilter ? new Set(selectedStatuses) : null;

    return allRuns.filter((run) => {
      // Status filter
      if (statusSet && !statusSet.has(run.status)) return false;

      // Version filter (draft vs published)
      if (versionFilter === 'draft' && !run.isDraftRun) return false;
      if (versionFilter === 'published' && run.isDraftRun) return false;

      // Date filter
      if (dateCutoffMs !== null && new Date(run.startTime).getTime() < dateCutoffMs) {
        return false;
      }

      // Search filter — run ID, workflow name, and status
      if (hasQuery) {
        const id = run.id.toLowerCase();
        if (id.includes(trimmedQuery)) return true;
        const wf = run.workflowName.toLowerCase();
        if (wf.includes(trimmedQuery)) return true;
        const status = run.status.toLowerCase();
        if (status.includes(trimmedQuery)) return true;
        return false;
      }

      return true;
    });
  }, [allRuns, selectedStatuses, versionFilter, datePreset, customTimeValue, customTimeUnit, deferredSearchQuery]);

  // Check if user is typing in an input field
  const isTypingInInput = useCallback(() => {
    const el = document.activeElement;
    return el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el?.getAttribute('contenteditable') === 'true';
  }, []);

  // Roving focus: when keyboard focus is already on a row, arrow keys move DOM focus too.
  const moveFocusToRun = useCallback((run: Run | undefined) => {
    if (!run) return;
    const el = runCardRefs.current.get(run.id);
    if (!el) return;
    if (runsListRef.current?.contains(document.activeElement)) el.focus({ preventScroll: true });
    el.scrollIntoView({ block: 'nearest' });
  }, []);

  // Keyboard navigation handler
  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (filteredRuns.length === 0) return;

    switch (event.key) {
      case 'j':
      case 'ArrowDown': {
        if (isTypingInInput()) return;
        event.preventDefault();
        const nextIndex = focusedIndex < filteredRuns.length - 1 ? focusedIndex + 1 : focusedIndex;
        setFocusedIndex(nextIndex);
        moveFocusToRun(filteredRuns[nextIndex]);
        break;
      }
      case 'k':
      case 'ArrowUp': {
        if (isTypingInInput()) return;
        event.preventDefault();
        const prevIndex = focusedIndex > 0 ? focusedIndex - 1 : 0;
        setFocusedIndex(prevIndex);
        moveFocusToRun(filteredRuns[prevIndex]);
        break;
      }
      case 'r': {
        if (isTypingInInput()) return;
        event.preventDefault();
        refetch();
        break;
      }
      case 'n': {
        if (isTypingInInput()) return;
        event.preventDefault();
        setIsTriggerDialogOpen(true);
        break;
      }
      case 'Escape': {
        event.preventDefault();
        setFocusedIndex(-1);
        // Blur current focus
        (document.activeElement as HTMLElement)?.blur();
        break;
      }
    }
  }, [filteredRuns, focusedIndex, refetch, isTypingInInput, moveFocusToRun]);

  // Reset focused index when filtered runs change
  useEffect(() => {
    setFocusedIndex(-1);
  }, [filteredRuns.length]);

  // Exactly one row is tabbable so Tab enters the listbox (focused > viewed > first row).
  const tabStopIndex = useMemo(() => {
    if (focusedIndex >= 0 && focusedIndex < filteredRuns.length) return focusedIndex;
    const viewedIndex = filteredRuns.findIndex((run) => run.id === selectedRunId);
    return viewedIndex >= 0 ? viewedIndex : 0;
  }, [focusedIndex, filteredRuns, selectedRunId]);

  // Keyboard (Tab) entry onto a row adopts it as the keyboard-focused row; pointer focus keeps the existing mouse visuals.
  const pointerFocusRef = useRef(false);
  const handleRunsListPointerDown = useCallback(() => {
    pointerFocusRef.current = true;
  }, []);
  const handleRunsListPointerUp = useCallback(() => {
    pointerFocusRef.current = false;
  }, []);
  const handleRunsListFocus = useCallback((event: React.FocusEvent<HTMLDivElement>) => {
    if (pointerFocusRef.current) return;
    const index = filteredRuns.findIndex((run) => runCardRefs.current.get(run.id) === event.target);
    if (index >= 0) setFocusedIndex(index);
  }, [filteredRuns]);

  const handleStatusToggle = useCallback((status: RunStatus) => {
    setSelectedStatuses((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
    );
  }, []);

  const handleClearFilters = useCallback(() => {
    setSelectedStatuses([]);
    setVersionFilter('all');
    setDatePreset('all');
    setSearchQuery('');
  }, []);

  // Stable ref-callback factory: parent owns the runCardRefs map, but each card gets a
  // callback bound to its own id. Memoizing this lets RunCard's React.memo do its job.
  const handleRunCardRef = useCallback((id: string, el: HTMLDivElement | null) => {
    if (el) {
      runCardRefs.current.set(id, el);
    } else {
      runCardRefs.current.delete(id);
    }
  }, []);

  // Hoisted map of status -> dot class. Memoized on the (stable) styles reference so we
  // don't allocate a fresh record per call.
  const dotClassesByStatus = useMemo<Record<RunStatus, string>>(
    () => ({
      succeeded: styles.statusDotSuccess,
      failed: styles.statusDotFailed,
      running: styles.statusDotRunning,
      cancelled: styles.statusDotCancelled,
    }),
    [styles]
  );

  const getStatusDotClass = useCallback(
    (status: RunStatus) => mergeClasses(styles.statusDot, dotClassesByStatus[status]),
    [styles, dotClassesByStatus]
  );

  if (isLoading) {
    return (
      <div className={styles.container}>
        <FiltersPanel<RunStatus, VersionFilter, DatePreset>
          idPrefix="runs-filters"
          activeFilterCount={0}
          onClear={() => undefined}
        />
        <div className={styles.divider} />
        <MonitoringListHeader count={0} unit="run" />
        {/* Skeleton loading */}
        <div className={styles.runsList}>
          <RunCardSkeleton />
          <RunCardSkeleton />
          <RunCardSkeleton />
          <RunCardSkeleton />
          <RunCardSkeleton />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container} onKeyDown={handleKeyDown} tabIndex={-1}>
      <FiltersPanel<RunStatus, VersionFilter, DatePreset>
        idPrefix="runs-filters"
        activeFilterCount={activeFilterCount}
        onClear={handleClearFilters}
        collapsedSummary={
          hasActiveFilters ? (
            <div className={mergeClasses(styles.activeFiltersRow, styles.activeFiltersRowPadding)}>
              {deferredSearchQuery.trim() && (
                <span className={styles.activeFilterTag}>
                  <SearchRegular fontSize={12} />
                  "{deferredSearchQuery}"
                </span>
              )}
              {selectedStatuses.map((status) => (
                <span key={status} className={styles.activeFilterTag}>
                  <span className={getStatusDotClass(status)} />
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </span>
              ))}
              {datePreset !== 'all' && (
                <span className={styles.activeFilterTag}>
                  {getTimeRangeLabel(datePreset, customTimeValue, customTimeUnit)}
                </span>
              )}
              <Button
                appearance="subtle"
                size="small"
                icon={<DismissRegular />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleClearFilters();
                }}
                className={styles.clearFiltersButton}
                aria-label="Clear all filters"
              />
            </div>
          ) : undefined
        }
        search={{
          label: 'Search',
          placeholder: 'Search by run ID or status...',
          value: searchQuery,
          onChange: setSearchQuery,
        }}
        status={{
          label: 'Status',
          options: STATUS_OPTIONS,
          selected: selectedStatuses,
          onToggle: handleStatusToggle,
        }}
        version={{
          label: 'Version',
          options: VERSION_OPTIONS,
          selected: versionFilter,
          onChange: setVersionFilter,
        }}
        timeRange={{
          label: 'Time Range',
          options: DATE_PRESET_OPTIONS,
          selected: datePreset,
          onChange: setDatePreset,
          customRow:
            datePreset === 'custom' ? (
              <div className={styles.customTimeRow}>
                <Text size={200} className={styles.lastLabel}>
                  Last
                </Text>
                <Input
                  type="number"
                  min={1}
                  max={999}
                  value={customTimeValue.toString()}
                  onChange={(_, data) => setCustomTimeValue(parseInt(data.value) || 1)}
                  className={styles.customTimeInput}
                  size="small"
                />
                <Select
                  value={customTimeUnit}
                  onChange={(_, data) => setCustomTimeUnit(data.value as TimeUnit)}
                  className={styles.customTimeSelect}
                  size="small"
                >
                  <option value="hours">hours</option>
                  <option value="days">days</option>
                </Select>
              </div>
            ) : undefined,
        }}
      />

      {/* Spacer */}
      <div className={styles.divider} />

      {/* List Header */}
      <MonitoringListHeader
        count={filteredRuns.length}
        unit="run"
        onRefresh={() => refetch()}
        isRefreshing={isRefetching}
        refreshAriaLabel={isRefetching ? 'Refreshing runs' : 'Refresh runs'}
        actions={
          <>
          {hasEditPermission && allRuns && allRuns.length > 0 && (
            <Tooltip content="Select runs to retry" relationship="description">
              <ToggleButton
                appearance="subtle"
                size="small"
                icon={<SelectAllOnRegular />}
                checked={isSelectionMode}
                onClick={handleSelectionModeChange}
                disabled={isBulkRetryPending}
                aria-label="Select runs"
                data-testid="runs-multiselect-toggle"
              />
            </Tooltip>
          )}
          {hideRunButton && onStartChat ? (
            <button
              className={styles.actionButton}
              onClick={onStartChat}
              disabled={isStartingChat}
              type="button"
            >
              <ChatRegular fontSize={14} />
              <span>{isStartingChat ? 'Starting...' : 'Chat'}</span>
            </button>
          ) : !hideRunButton ? (
            <Tooltip
              content={isDraftMode ? 'Test draft workflow' : 'Run workflow'}
              relationship="label"
            >
              <button
                className={styles.actionButton}
                onClick={() => setIsTriggerDialogOpen(true)}
                type="button"
              >
                <PlayFilled fontSize={14} />
                <span>{isDraftMode ? 'Test draft' : 'Run'}</span>
              </button>
            </Tooltip>
          ) : undefined}
          </>
        }
      />

      {isSelectionMode && (
        <RunSelectionToolbar
          selectedCount={checkedRuns.length}
          eligibleCount={eligibleRetryItems.length}
          isPending={isBulkRetryPending}
          onRetry={handleBulkRetry}
          onClear={() => setCheckedRunIds(new Set())}
        />
      )}

      {/* Trigger Workflow Dialog */}
      <TriggerWorkflowDialog
        open={isTriggerDialogOpen}
        onClose={() => setIsTriggerDialogOpen(false)}
        onSuccess={() => refetch()}
        {...(isDraftMode && { onSaveDraft: handleSaveDraft })}
        {...(isDraftMode && { onStreamingDraftTrigger: handleStreamingDraftTrigger })}
        {...(workflow ? { workflowTriggers: workflow.definition.triggers as Record<string, { type: string; kind?: string; inputs?: Record<string, unknown> }> } : {})}
      />

      {/* Runs List */}
      {filteredRuns.length === 0 ? (
        hasActiveFilters ? (
          <MonitoringEmptyState
            icon={<FilterDismissRegular />}
            title="No matching runs"
            subtitle="No runs match your filters. Try adjusting or clearing them."
            actions={
              <Button
                appearance="primary"
                size="small"
                icon={<DismissRegular />}
                onClick={handleClearFilters}
              >
                Clear filters
              </Button>
            }
          />
        ) : allRuns && allRuns.length === 0 ? (
          <MonitoringEmptyState
            icon={<HistoryRegular />}
            title="No runs yet"
            subtitle={
              hideRunButton
                ? 'Start a chat to see execution history.'
                : 'Run this workflow to see execution history.'
            }
            actions={
              hideRunButton && onStartChat ? (
                <Button
                  appearance="primary"
                  size="small"
                  icon={<ChatRegular />}
                  onClick={onStartChat}
                  disabled={isStartingChat}
                >
                  {isStartingChat ? 'Starting...' : 'Start Chat'}
                </Button>
              ) : !hideRunButton ? (
                <Button
                  appearance="primary"
                  size="small"
                  icon={<PlayFilled />}
                  onClick={() => setIsTriggerDialogOpen(true)}
                >
                  Run workflow
                </Button>
              ) : undefined
            }
          />
        ) : (
          <MonitoringEmptyState
            icon={<HistoryRegular />}
            title="No runs found"
            subtitle="Run this workflow to see execution history."
          />
        )
      ) : (
        <div
          className={styles.runsList}
          ref={runsListRef}
          role="listbox"
          aria-label="Workflow runs"
          aria-multiselectable={isSelectionMode || undefined}
          onFocus={handleRunsListFocus}
          onPointerDown={handleRunsListPointerDown}
          onPointerUp={handleRunsListPointerUp}
          onPointerCancel={handleRunsListPointerUp}
        >
          {filteredRuns.map((run, index) => (
            <RunCard
              key={run.id}
              run={run}
              isSelected={run.id === selectedRunId}
              isFocused={index === focusedIndex}
              isTabStop={index === tabStopIndex}
              onSelect={handleRowActivate}
              onRef={handleRunCardRef}
              selectionMode={isSelectionMode}
              isChecked={checkedRunIds.has(run.id)}
            />
          ))}

          {/* Infinite scroll sentinel */}
          <div ref={loadMoreRef} className={styles.loadMoreSentinel} />

          {/* Loading more indicator */}
          {isFetchingNextPage && (
            <div className={styles.loadMoreIndicator}>
              <Spinner size="tiny" label="Loading more..." />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
